import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, formatApiErrorDetail, friendlyError } from "../lib/api";
import { toast } from "sonner";

const FIELDS = [
  { key: "title", label: "Název", type: "text", maxLength: 120 },
  { key: "description", label: "Popis", type: "textarea", maxLength: 400 },
  { key: "city", label: "Město", type: "text", maxLength: 80 },
  { key: "date_text", label: "Datum a čas", type: "text", maxLength: 80, placeholder: "15:00 17.6.2026" },
  { key: "capacity", label: "Kapacita", type: "number", min: 2, max: 10000 },
  { key: "age_min", label: "Věk od (volitelné)", type: "number", min: 0, max: 120 },
  { key: "age_max", label: "Věk do (volitelné)", type: "number", min: 0, max: 120 },
  { key: "perks", label: "Co dostane účastník (volitelné)", type: "text", maxLength: 200 },
  { key: "country_note", label: "Poznámka k zemi (volitelné)", type: "text", maxLength: 120 },
  { key: "organizer_note", label: "O organizátorovi", type: "textarea", maxLength: 400 },
];

export default function EditEventDialog({ event, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!event) return;
    setForm({
      title: event.title || "",
      description: event.description || "",
      city: event.city || "",
      date_text: event.date_text || "",
      capacity: event.capacity || 2,
      age_min: event.age_min || 0,
      age_max: event.age_max || 0,
      perks: event.perks || "",
      country_note: event.country_note || "",
      organizer_note: event.organizer_note || "",
    });
    setError("");
  }, [event, open]);

  if (!event) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...form };
      ["capacity", "age_min", "age_max"].forEach((k) => {
        payload[k] = parseInt(payload[k], 10) || 0;
      });
      await api.patch(`/events/${event.id}`, payload);
      toast.success("Změny uloženy. Účastníkům se zobrazí v chatu.");
      onSaved?.();
      onClose();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="edit-event-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold">Upravit akci</DialogTitle>
          <p className="text-sm text-slate-500 font-medium">Změny se okamžitě promítnou na stránce a v chatu se zobrazí systémová zpráva.</p>
        </DialogHeader>

        <div className="flex flex-col gap-4 mt-2">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea
                  data-testid={`edit-event-${f.key}`}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  maxLength={f.maxLength}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium resize-none"
                />
              ) : (
                <input
                  data-testid={`edit-event-${f.key}`}
                  type={f.type}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  maxLength={f.maxLength}
                  min={f.min} max={f.max}
                  placeholder={f.placeholder}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium"
                />
              )}
            </div>
          ))}

          {error && (
            <div data-testid="edit-event-error" className="rounded-xl bg-red-50 text-red-700 text-sm font-semibold p-3">{error}</div>
          )}

          <div className="flex gap-2 sticky bottom-0 bg-white pt-2">
            <button
              data-testid="edit-event-cancel-btn"
              onClick={onClose}
              className="flex-1 px-5 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-bold hover:border-slate-300"
            >Zavřít</button>
            <button
              data-testid="edit-event-save-btn"
              onClick={save} disabled={saving}
              className="flex-1 px-5 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white font-bold transition-all active:scale-95"
            >{saving ? "Ukládám…" : "Uložit změny"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
